import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LimaHeader from "../components/LimaHeader";
import { api, API, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import RiskBadge from "../components/RiskBadge";
import { toast } from "sonner";
import { FileDown, Sparkles, ArrowLeft, TrendingUp, AlertTriangle, ShieldCheck } from "lucide-react";

export default function Report() {
  const { diagId } = useParams();
  const nav = useNavigate();
  const [diag, setDiag] = useState(null);
  const [company, setCompany] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.get(`/admin/diagnostics/${diagId}`).then(r => {
      setDiag(r.data);
      setCompany(r.data.company);
      if (r.data.ai_summary) setAi({ summary: r.data.ai_summary, action_plan: r.data.ai_plan || "" });
    }).catch(() => toast.error("Diagnóstico não encontrado"));
  }, [diagId]);

  const runAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/diagnostics/${diagId}/ai-report`);
      setAi(data);
      toast.success("Sumário gerado por IA");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro na IA");
    } finally { setAiLoading(false); }
  };

  const downloadPdf = () => {
    const url = `${API}/diagnostics/${diagId}/pdf?token=${getToken()}`;
    window.open(url, "_blank");
  };

  if (!diag || !company) return <div className="p-10 text-slate-500">Carregando...</div>;

  const r = diag.result;
  const levelColor = r.color === "red" ? "text-red-700 bg-red-50 border-red-200" :
                     r.color === "orange" ? "text-orange-700 bg-orange-50 border-orange-200" :
                     "text-emerald-700 bg-emerald-50 border-emerald-200";
  const fineColor = r.fine_band === "ALTO" ? "text-red-700 bg-red-50 border-red-200" :
                    r.fine_band === "MÉDIO" ? "text-orange-700 bg-orange-50 border-orange-200" :
                    "text-emerald-700 bg-emerald-50 border-emerald-200";

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <LimaHeader />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
          <Button variant="ghost" onClick={() => nav("/dashboard")} data-testid="back-dash">
            <ArrowLeft size={16} className="mr-2" /> Painel
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={runAi} disabled={aiLoading}
                    className="border-[#c7a45a] text-[#10243f]" data-testid="ai-report-btn">
              <Sparkles size={16} className="mr-2" />
              {aiLoading ? "Gerando..." : ai ? "Regerar análise IA" : "Análise executiva (IA)"}
            </Button>
            <Button onClick={downloadPdf}
                    className="bg-[#10243f] hover:bg-[#19385e] text-white" data-testid="pdf-btn">
              <FileDown size={16} className="mr-2" /> Baixar PDF
            </Button>
          </div>
        </div>

        {/* Company + Level card */}
        <div className="bg-white border border-slate-200 rounded-xl p-8 mb-6">
          <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">RESULTADO PRELIMINAR</span>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#10243f] mt-1">
            {company.razao_social}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{company.cnpj} · {company.empregados} empregados · Gerado em {new Date(diag.created_at).toLocaleString("pt-BR")}</p>

          <div className="grid md:grid-cols-4 gap-4 mt-8">
            <div className="border border-slate-200 rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Índice preliminar</div>
              <div className="font-display font-black text-4xl text-[#10243f] mt-2">{r.score}%</div>
              <div className="text-xs text-slate-500 mt-1">100 = conformidade plena</div>
            </div>
            <div className={`border rounded-lg p-5 ${levelColor}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Nível de exposição</div>
              <div className="font-display font-black text-3xl mt-2">{r.level}</div>
              <div className="text-xs opacity-80 mt-1">
                <TrendingUp size={12} className="inline mr-1" />{r.red} altas · {r.orange} médias · {r.yellow} atenção
              </div>
            </div>
            <div className={`border rounded-lg p-5 ${fineColor}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Risco de multa</div>
              <div className="font-display font-black text-3xl mt-2">{r.fine_band}</div>
              <div className="text-xs opacity-80 mt-1"><AlertTriangle size={12} className="inline mr-1" />Faixa qualitativa</div>
            </div>
            <div className="border border-slate-200 rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Módulos NR ativos</div>
              <div className="font-display font-black text-4xl text-[#c7a45a] mt-2">{r.active_modules.length}</div>
              <div className="text-xs text-slate-500 mt-1"><ShieldCheck size={12} className="inline mr-1" />Aplicáveis ao negócio</div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">
            <strong className="text-[#10243f]">Interpretação da faixa de multa:</strong> {r.fine_desc}
          </div>
        </div>

        {/* Active modules chips */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Módulos aplicáveis</div>
          <div className="flex flex-wrap gap-2">
            {r.active_modules.map(m => (
              <span key={m} className="px-3 py-1.5 text-xs font-semibold bg-[#10243f] text-white rounded-md">{m}</span>
            ))}
          </div>
        </div>

        {/* AI report block */}
        {ai && (
          <div className="bg-gradient-to-br from-[#10243f] to-[#19385e] text-white rounded-xl p-8 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-[#c7a45a]" />
              <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">SUMÁRIO EXECUTIVO · IA</span>
            </div>
            <div className="prose prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-white/95 leading-relaxed">{ai.summary}</div>
            </div>
            {ai.action_plan && (
              <>
                <div className="mt-6 mb-2 text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">PLANO DE MITIGAÇÃO</div>
                <div className="whitespace-pre-wrap text-white/95 leading-relaxed">{ai.action_plan}</div>
              </>
            )}
          </div>
        )}

        {/* Risk matrix table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-display font-bold text-xl text-[#10243f]">Mapa de exposições</h2>
            <p className="text-sm text-slate-500">Cada linha aponta a NR, situação atual e mitigação recomendada.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[#10243f]">
                <tr>
                  <th className="text-left p-3 font-semibold">NR</th>
                  <th className="text-left p-3 font-semibold">Área / Item</th>
                  <th className="text-left p-3 font-semibold">Situação</th>
                  <th className="text-left p-3 font-semibold">Exposição</th>
                  <th className="text-left p-3 font-semibold">Mitigação recomendada</th>
                </tr>
              </thead>
              <tbody>
                {r.risks.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-500">
                    Nenhuma lacuna preliminar identificada. Ainda assim, recomenda-se validação técnica.
                  </td></tr>
                )}
                {r.risks.map((risk, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs">{risk.nr}</td>
                    <td className="p-3">{risk.area}</td>
                    <td className="p-3 text-slate-600">{risk.status}</td>
                    <td className="p-3"><RiskBadge level={risk.level} /></td>
                    <td className="p-3 text-slate-700">{risk.mitigation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-6 text-center">
          Este documento é uma triagem preliminar. Não constitui certificação nem garantia de inexistência de autuações.
        </p>
      </main>
    </div>
  );
}
